import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import MaterialReactTable, {
  MRT_GlobalFilterTextField,
  MRT_ToggleFiltersButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_FullScreenToggleButton,
  MRT_ToggleGlobalFilterButton,
} from 'material-react-table';
import { FaSort } from 'react-icons/fa6';
import { useTheme } from '@mui/material/styles';

const defaultHeadCellSx = (theme) => ({
  fontSize: theme.typography.subtitle1.fontSize,
  backgroundColor: '#f8fafc',
  color: '#475569 !important',
  fontWeight: 600,
  px: 0,
  paddingLeft: 0.7,
  py: 2,
  minHeight: '3.3rem',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
  textAlign: 'left',
  textTransform: 'none',
  letterSpacing: 'normal',
  position: 'sticky',
  top: 0,
  zIndex: 2,
  borderBottom: '1px solid #e2e8f0',
  '&:first-of-type': {
    paddingLeft: '24px',
  },
  '& .Mui-TableHeadCell-Content': {
    justifyContent: 'flex-start !important',
    width: '100%',
    gap: '0.5rem',
    color: '#475569 !important',
  },
  '& .Mui-TableHeadCell-Content-Labels': {
    justifyContent: 'flex-start',
    minWidth: 0,
    color: '#475569 !important',
  },
  '& .MuiBox-root': {
    justifyContent: 'flex-start !important',
    width: 'max-content',
  },
  '& .MuiTypography-root': {
    color: '#475569 !important',
    fontWeight: 600,
    letterSpacing: 'normal',
    textTransform: 'none',
  },
  '& .MuiButtonBase-root': {
    color: '#94a3b8 !important',
  },
  '& svg': {
    color: '#94a3b8 !important',
    fill: '#94a3b8 !important',
  },
});

const defaultBodyCellSx = (theme) => ({
  fontSize: theme.typography.body2.fontSize,
  color: '#1e293b',
  fontWeight: 400,
  borderBottom: '1px solid #e2e8f0',
  px: 0.7,
  py: 1.5,
  '&:first-of-type': {
    paddingLeft: '24px',
  },
  verticalAlign: 'middle',
  textAlign: 'left',
});

const mergeSx = (baseSx, overrideSx) => ({
  ...(baseSx || {}),
  ...(overrideSx || {}),
});

const buildRowProps = ({ row, onRowClick, selectedRowId, baseProps }) => {
  const rowProps = typeof baseProps === 'function' ? baseProps({ row }) : (baseProps || {});
  const clickProps = onRowClick
    ? {
      onClick: () => onRowClick(row.original, row),
      sx: { cursor: 'pointer' },
    }
    : {};
  const selectedProps = selectedRowId ? { selected: row.id === selectedRowId } : {};

  return {
    ...rowProps,
    ...clickProps,
    ...selectedProps,
    sx: {
      transition: 'background-color 0.12s ease',
      '&:hover td': { },
      ...(rowProps.sx || {}),
      ...(clickProps.sx || {}),
      ...(selectedProps.sx || {}),
    },
  };
};

const GlobalMaterialTable = ({
  columns,
  data,
  isLoading,
  error,
  emptyMessage,
  onRowClick,
  selectedRowId,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  rowCount,
  manualPagination,
  muiTableBodyRowProps,
  muiTableHeadCellProps,
  muiTableBodyCellProps,
  muiTablePaperProps,
  muiTableContainerProps,
  enablePagination,
  enableBottomToolbar,
  enableTopToolbar,
  enableGlobalFilter= false,
  enableColumnFilters,
  enableDensityToggle,
  enableColumnActions,
  enableHiding,
  enableFullScreenToggle,
  initialState,
  state,
  muiTopToolbarProps,
  tableInstanceRef,
  tableTitle,
  tableCount,
  OuterBorder,
  OuterBoxShadow,
  renderTopToolbarCustomActions,
  muiTablePaginationProps,
  renderRightToolbarCustomActions,
  ...rest
}) => {
  const hasPagination = page !== undefined || enablePagination;
  const controlledPagination = state?.pagination
    || (page !== undefined ? { pageIndex: Math.max(page - 1, 0), pageSize: pageSize || 10 } : undefined)
    || initialState?.pagination
    || { pageIndex: 0, pageSize: pageSize || 10 };
  const paginationState = page !== undefined
    ? { pageIndex: Math.max(page - 1, 0), pageSize: pageSize || 10 }
    : undefined;
  const tableState = {
    isLoading: Boolean(isLoading),
    showSkeletons: Boolean(isLoading),
    showProgressBars: false,
    showLoadingOverlay: false,
    ...(paginationState ? { pagination: paginationState } : {}),
    ...(state || {}),
  };
  const theme = useTheme();

  const mergedInitialState = {
    density: 'compact',
    ...(initialState || {}),
  };

  const customIcons = {
    SortIcon: (props) => <FaSort {...props} style={{  color: '#94a3b8' }} />,
    SyncAltIcon: (props) => <FaSort {...props} style={{  color: '#94a3b8' }} />,
  };

  const mergedPaperProps = {
    ...muiTablePaperProps,
    elevation: 0,
    sx: {
      borderRadius: '16px',
      border: OuterBorder ? OuterBorder : '1px solid #e2e8f0',
      overflow: 'hidden',
      backgroundColor: '#ffffff',
      boxShadow: OuterBoxShadow ? OuterBoxShadow : '0 8px 32px rgba(0,0,0,0.04)',
      ...(muiTablePaperProps?.sx || {}),
    },
  };

  const mergedTopToolbarProps = {
    ...muiTopToolbarProps,
    sx: {
      minHeight: 'unset',
      borderBottom: '1px solid #e2e8f0',
      backgroundColor: '#ffffff',
      borderTopLeftRadius: '16px',
      borderTopRightRadius: '16px',
      // Target the inner Box that MRT uses to layout the toolbar content
      '& > .MuiBox-root': {
        alignItems: 'center !important',
        padding: '12px 16px !important',
      },
      '& .MuiToolbar-root': {
        alignItems: 'center'
      },
      ...(muiTopToolbarProps?.sx || {}),
    },
  };

  const mergedHeadCellProps = {
    ...muiTableHeadCellProps,
    sx: mergeSx(defaultHeadCellSx(theme), muiTableHeadCellProps?.sx),
  };

  const mergedBodyCellProps = {
    ...muiTableBodyCellProps,
    sx: mergeSx(defaultBodyCellSx(theme), muiTableBodyCellProps?.sx),
  };

  const mergedTableContainerProps = {
    ...muiTableContainerProps,
    sx: {
      // maxHeight: 'calc(100vh - 200px)',
      overflowX: 'auto',
      '& .MuiTableRow-root': {
        boxShadow: 'none !important',
      },
      ...(muiTableContainerProps?.sx || {}),
    },
  };

  const mergedBodyRowProps = ({ row }) => buildRowProps({
    row,
    onRowClick,
    selectedRowId,
    baseProps: muiTableBodyRowProps,
  });

  const combinedRenderTopToolbarCustomActions = (props) => {
    const customActions = renderTopToolbarCustomActions ? renderTopToolbarCustomActions(props) : null;
    if (!tableTitle && !customActions) return undefined;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {tableTitle && (
          <Typography sx={{ fontWeight: 600,  color: '#111827' }}>
            {tableTitle} {tableCount !== undefined && tableCount !== null && tableCount > 0 ? `(${tableCount})` : ''}
          </Typography>
        )}
        {customActions}
      </Box>
    );
  };
  
  return (
    <MaterialReactTable
      columns={columns}
      data={Array.isArray(data) ? data : []}
      localization={{ filterByColumn: 'Filter by {column}' }}
      icons={{ ...customIcons, ...(rest.icons || {}) }}
      renderColumnActionsMenuItems={({ internalColumnMenuItems }) => internalColumnMenuItems.filter(
        (item) => !String(item.props?.label).toLowerCase().includes('sort')
      )}
      enableSorting
      enableColumnActions={enableColumnActions}
      enableColumnFilters={enableColumnFilters}
      enableDensityToggle={enableDensityToggle}
      enableFullScreenToggle={enableFullScreenToggle}
      enableGlobalFilter={enableGlobalFilter}
      enableHiding={enableHiding}
      enableTopToolbar={enableTopToolbar}
      positionGlobalFilter="none"
      enablePagination={hasPagination}
      enableBottomToolbar={enableBottomToolbar ?? hasPagination}
      manualPagination={manualPagination ?? Boolean(page !== undefined)}
      rowCount={rowCount}
      renderTopToolbarCustomActions={tableTitle || renderTopToolbarCustomActions ? combinedRenderTopToolbarCustomActions : undefined}
      renderToolbarInternalActions={({ table }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {enableGlobalFilter !== false && <MRT_GlobalFilterTextField table={table} />}
          {enableGlobalFilter !== false && <MRT_ToggleGlobalFilterButton table={table} />}
          {enableColumnFilters !== false && <MRT_ToggleFiltersButton table={table} />}
          {enableHiding !== false && <MRT_ShowHideColumnsButton table={table} />}
          {enableDensityToggle !== false && <MRT_ToggleDensePaddingButton table={table} />}
          {enableFullScreenToggle !== false && <MRT_FullScreenToggleButton table={table} />}
          {renderRightToolbarCustomActions && renderRightToolbarCustomActions({ table })}
        </Box>
      )}
      onPaginationChange={onPageChange || onPageSizeChange
        ? (updater) => {
          const next = typeof updater === 'function'
            ? updater(controlledPagination)
            : updater;
          if (
            onPageChange
            && next?.pageIndex !== undefined
            && next.pageIndex !== controlledPagination.pageIndex
          ) {
            onPageChange(next.pageIndex + 1);
          }
          if (
            onPageSizeChange
            && next?.pageSize !== undefined
            && next.pageSize !== controlledPagination.pageSize
          ) {
            onPageSizeChange(next.pageSize);
          }
        }
        : undefined}
      initialState={mergedInitialState}
      state={tableState}
      enableLoadingOverlay={false}
      renderLoadingOverlay={() => null}
      muiLinearProgressProps={{
        sx: { display: "none" },
      }}
      renderEmptyRowsFallback={() => (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#94a3b8',  }}>
            {emptyMessage}
          </Typography>
        </Box>
      )}
      muiTablePaperProps={mergedPaperProps}
      muiTopToolbarProps={mergedTopToolbarProps}
      muiTableHeadCellProps={mergedHeadCellProps}
      muiSearchTextFieldProps={{
        placeholder: 'Search in results...',
        variant: 'outlined',
        size: 'small',
        sx: {
          minWidth: '300px',
          '& .MuiInputBase-root': {
            mr: 1,
            mt: 0.4,
          },
          '& .MuiInputAdornment-root': { mt: '0 !important' },
          '& .MuiSvgIcon-root': { mt: '0 !important' }
        }
      }}
      muiTableContainerProps={mergedTableContainerProps}
      muiTableBodyRowProps={mergedBodyRowProps}
      muiTableBodyCellProps={mergedBodyCellProps}
      muiTablePaginationProps={{
        ...muiTablePaginationProps,
        sx: {
          '& .MuiTablePagination-selectLabel': {
            marginBottom: 0,
          },
          '& .MuiTablePagination-displayedRows': {
            marginBottom: 0,
          },
          '& .MuiTablePagination-select': {
            paddingRight: '36px !important',
            paddingTop: '8px !important',
            paddingBottom: '8px !important',
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiSelect-select.MuiTablePagination-select': {
            paddingRight: '36px !important',
          },
          '& .MuiTablePagination-selectIcon': {
            right: '10px',
          },
        },
      }}
      tableInstanceRef={tableInstanceRef}
      {...rest}
    />
  );
};

GlobalMaterialTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  emptyMessage: PropTypes.string,
  onRowClick: PropTypes.func,
  selectedRowId: PropTypes.string,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  rowCount: PropTypes.number,
  manualPagination: PropTypes.bool,
  muiTableBodyRowProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  muiTableHeadCellProps: PropTypes.object,
  muiTableBodyCellProps: PropTypes.object,
  muiTablePaperProps: PropTypes.object,
  muiTableContainerProps: PropTypes.object,
  enablePagination: PropTypes.bool,
  enableBottomToolbar: PropTypes.bool,
  enableTopToolbar: PropTypes.bool,
  enableGlobalFilter: PropTypes.bool,
  enableColumnFilters: PropTypes.bool,
  enableDensityToggle: PropTypes.bool,
  enableColumnActions: PropTypes.bool,
  enableHiding: PropTypes.bool,
  enableFullScreenToggle: PropTypes.bool,
  initialState: PropTypes.object,
  state: PropTypes.object,
  muiTopToolbarProps: PropTypes.object,
  tableInstanceRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any })
  ]),
  renderTopToolbarCustomActions: PropTypes.func,
  renderRightToolbarCustomActions: PropTypes.func,
};

GlobalMaterialTable.defaultProps = {
  data: [],
  isLoading: false,
  error: '',
  emptyMessage: 'No records found',
  onRowClick: undefined,
  selectedRowId: '',
  page: undefined,
  pageSize: 10,
  onPageChange: undefined,
  onPageSizeChange: undefined,
  rowCount: undefined,
  manualPagination: undefined,
  muiTableBodyRowProps: undefined,
  muiTableHeadCellProps: undefined,
  muiTableBodyCellProps: undefined,
  muiTablePaperProps: undefined,
  muiTableContainerProps: undefined,
  enablePagination: undefined,
  enableBottomToolbar: undefined,
  enableTopToolbar: true,
  enableGlobalFilter: true,
  enableColumnFilters: true,
  enableDensityToggle: true,
  enableColumnActions: true,
  enableHiding: true,
  enableFullScreenToggle: true,
  initialState: undefined,
  state: undefined,
  muiTopToolbarProps: undefined,
  renderTopToolbarCustomActions: undefined,
  renderRightToolbarCustomActions: undefined,
};

export default GlobalMaterialTable;
